import { useState } from "react";
import { Sparkles, Download, Store, X, Shield, Cpu, ChevronRight, Zap, TrendingUp, ChevronDown, FileText, FileSpreadsheet, FileType, ArrowLeft } from "lucide-react";

const MONO = "'JetBrains Mono', ui-monospace, monospace";
export const money = (n) => "$" + (n >= 1e6 ? (n / 1e6).toFixed(2) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : Number(n).toFixed(2));

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/[0.07] px-3 py-2">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-base font-bold text-slate-100" style={{ fontFamily: MONO }}>{value}</div>
  </div>
);

export function OutputPanel({ result, role, onClose, onOpenStores, onExport, exporting,
                             onSelectFinding, routingMode, setRoutingMode, width = 540, onResize }) {
  const [exportOpen, setExportOpen] = useState(false);
  const scopeLines = result?.scope_lines || [];
  const ci = result?.cost_intelligence;
  const qs = result?.quantum_summary || {};

  const startDrag = (e) => {
    e.preventDefault();
    const move = (ev) => {
      const w = Math.min(Math.max(window.innerWidth - ev.clientX, 380), Math.min(920, window.innerWidth - 340));
      onResize && onResize(w);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <div className="relative flex-shrink-0 h-full border-l border-white/[0.08] bg-[#0A0F1A]/95 backdrop-blur-2xl" style={{ width }} data-testid="output-panel">
      <div onMouseDown={startDrag} data-testid="panel-resize-handle" title="Drag to resize"
        className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-[#14F0C8]/50 active:bg-[#14F0C8] z-30 transition-colors" />
      <div className="h-full overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0A0F1A]/95 backdrop-blur-2xl border-b border-white/[0.06]">
        <div className="flex items-center gap-2 text-[#14F0C8]">
          <Sparkles className="w-4 h-4" />
          <span className="font-bold tracking-wide text-sm" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>QUANTUM OPTIMIZATION COMPLETE</span>
        </div>
        <button onClick={onClose} data-testid="result-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="px-6 py-4 space-y-4">
        {scopeLines.length > 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid="scope-readout">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Analysis scope</div>
            {scopeLines.map((s) => (
              <div key={s.label} className="flex justify-between text-xs py-0.5">
                <span className="text-slate-400">{s.label}</span>
                <span className="text-slate-200 text-right max-w-[62%]" style={{ fontFamily: MONO }}>{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {role !== "ceo" && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-400/90" data-testid="role-banner">
            <Shield className="w-3 h-3" /> {role === "vp_ops" ? "VP Operations view, regional scope" : (result.mode === "jnj" ? "Field Lead view, single site scope, financials restricted" : "Store Manager view, single-store scope, financials restricted")}
          </div>
        )}

        <div className="rounded-xl bg-[#14F0C8]/[0.08] border border-[#14F0C8]/25 px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Quantum uplift</div>
          {role === "store_manager"
            ? <div className="text-2xl font-bold text-slate-500">restricted</div>
            : <div className="text-4xl font-bold text-[#14F0C8]" style={{ fontFamily: MONO }} data-testid="uplift-value">{money(result.summary.quantum_uplift_usd)}</div>}
          <div className="text-[10px] text-slate-500 mt-0.5">classical equiv. compute: {result.summary.classical_equivalent}</div>
        </div>

        {result.executive_summary && (
          <div className="text-[13px] text-slate-300 leading-relaxed border-l-2 border-[#14F0C8]/50 pl-3" data-testid="exec-summary">
            {result.executive_summary}
          </div>
        )}

        {qs.variables ? (
          <div className="rounded-xl bg-white/[0.03] border border-[#14F0C8]/25 px-4 py-3 space-y-2" data-testid="quantum-applied">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#14F0C8] font-bold">
              <Zap className="w-3.5 h-3.5" /> Quantum Optimization Applied
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-lg font-bold text-slate-100" style={{ fontFamily: MONO }}>{qs.variables.toLocaleString()}</div><div className="text-[9px] uppercase tracking-wide text-slate-500">QUBO variables</div></div>
              <div><div className="text-lg font-bold text-slate-100" style={{ fontFamily: MONO }}>{qs.solve_time_s}s</div><div className="text-[9px] uppercase tracking-wide text-slate-500">Solve time</div></div>
              <div><div className="text-lg font-bold text-slate-100" style={{ fontFamily: MONO }}>{qs.optimality_gap}</div><div className="text-[9px] uppercase tracking-wide text-slate-500">Optimality gap</div></div>
            </div>
            <div className="text-[11px] text-slate-300 leading-snug border-t border-white/[0.06] pt-2" style={{ fontFamily: MONO }} data-testid="quantum-comparison">{qs.comparison_line}</div>
            <div className="text-[10px] text-slate-500">{qs.hardware}</div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          <Stat label={result.mode === "jnj" ? "Sites analyzed" : "Stores analyzed"} value={(result.summary.stores_analyzed || 0).toLocaleString()} />
          <Stat label={result.mode === "jnj" ? "Compounds evaluated" : "SKUs evaluated"} value={(result.summary.skus_evaluated || 0).toLocaleString()} />
          <Stat label="Combinations" value={(result.summary.combinations_optimized || 0).toLocaleString()} />
          <Stat label="Solve time" value={`${result.summary.solve_time_seconds}s`} />
        </div>

        <div className={`grid ${result.mode === "jnj" ? "grid-cols-1" : "grid-cols-2"} gap-2 relative`}>
          {result.mode !== "jnj" && (
            <button onClick={onOpenStores}
              className="flex items-center justify-center gap-1.5 text-xs py-2.5 rounded-lg border border-white/[0.1] hover:border-[#14F0C8]/60 text-slate-200 transition-colors"
              data-testid="view-store-actions-btn">
              <Store className="w-3.5 h-3.5" /> Store-Level Actions
            </button>
          )}
          {role !== "store_manager" && (
            <div className="relative">
              <button onClick={() => setExportOpen((v) => !v)}
                className="w-full flex items-center justify-center gap-1.5 text-xs py-2.5 rounded-lg border border-white/[0.1] hover:border-[#14F0C8]/60 text-slate-200 transition-colors"
                data-testid="export-report-btn">
                <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-52 rounded-xl border border-white/[0.12] bg-[#0D1424] shadow-2xl z-20 overflow-hidden" data-testid="export-menu">
                  {[["pdf", FileText, "Export as PDF", "Board-ready deck"],
                    ["xlsx", FileSpreadsheet, "Export as Excel", "Analyst workbook"],
                    ["docx", FileType, "Export as Word", "Editable report"]].map(([fmt, Icon, label, sub]) => (
                    <button key={fmt} onClick={() => { setExportOpen(false); onExport(fmt); }}
                      disabled={exporting}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/[0.05] transition-colors disabled:opacity-50"
                      data-testid={`export-${fmt}`}>
                      <Icon className="w-4 h-4 text-[#14F0C8]" />
                      <div><div className="text-xs text-slate-200">{label}</div><div className="text-[10px] text-slate-500">{sub}</div></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Top findings. Click for the WHAT · WHY · HOW</div>
          <div className="space-y-2">
            {result.findings.map((f) => (
              <button key={f.rank} onClick={() => onSelectFinding(f)}
                className="w-full text-left rounded-xl bg-white/[0.03] border border-white/[0.07] hover:border-[#14F0C8]/40 p-3 transition-colors group"
                data-testid={`finding-${f.rank}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="text-[13px] font-semibold text-slate-100">{f.rank}. {f.title}</div>
                  <div className="text-sm font-bold text-emerald-400 whitespace-nowrap" style={{ fontFamily: MONO }}>+{money(f.impact_usd)}</div>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">{f.detail}</div>
                <div className="flex items-center gap-1 text-[10px] text-[#14F0C8] mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  View execution playbook <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {role !== "store_manager" && (
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-3 text-xs" style={{ fontFamily: MONO }}>
            <div className="flex justify-between"><span className="text-slate-400">Classical optimizer</span><span>{money(result.comparison.classical_value_usd)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Quantum optimizer</span><span className="text-[#14F0C8]">{money(result.comparison.quantum_value_usd)}</span></div>
            <div className="flex justify-between font-bold mt-1 pt-1 border-t border-white/[0.08]"><span>Quantum uplift</span><span className="text-emerald-400">+{money(result.comparison.uplift_usd)} ({result.comparison.uplift_pct}%)</span></div>
          </div>
        )}

        {ci && role !== "store_manager" && (
          <CostIntelligence ci={ci} routingMode={routingMode} setRoutingMode={setRoutingMode} />
        )}

        <div className="flex items-center gap-2 text-[10px] text-amber-400/80"><Cpu className="w-3 h-3" /> {result.compute_method.label}</div>
        <div className="text-[10px] text-slate-600 italic">{result.data_disclaimer}</div>
      </div>
      </div>
    </div>
  );
}

export function CostIntelligence({ ci, routingMode, setRoutingMode }) {
  const [open, setOpen] = useState(true);
  const displayed = ci.modes[routingMode] ?? ci.actual_cost ?? 0;
  const savedVsMax = (ci.modes.maximum - displayed);
  const savedPct = ci.modes.maximum ? Math.round((1 - displayed / ci.modes.maximum) * 100) : 0;
  return (
    <div className="rounded-xl border border-[#8B5CF6]/25 bg-[#8B5CF6]/[0.05]" data-testid="cost-intelligence">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[#c4b5fd] text-xs font-semibold"><Zap className="w-3.5 h-3.5" /> COST INTELLIGENCE</div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* routing mode toggle */}
          <div className="flex gap-1 rounded-lg bg-black/30 p-1" data-testid="routing-mode-toggle">
            {[["smart", "Smart"], ["premium", "Premium"], ["maximum", "Maximum"]].map(([k, l]) => (
              <button key={k} onClick={() => setRoutingMode(k)}
                className={`flex-1 text-[11px] py-1 rounded-md transition-colors ${routingMode === k ? "bg-[#8B5CF6] text-white font-semibold" : "text-slate-400 hover:text-slate-200"}`}
                data-testid={`routing-${k}`}>{l}</button>
            ))}
          </div>
          <div className="rounded-lg bg-black/20 p-3" style={{ fontFamily: MONO }}>
            <div className="flex justify-between text-xs"><span className="text-slate-400">This run</span><span className="text-slate-100 font-bold" data-testid="cost-actual">${displayed.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs mt-0.5"><span className="text-slate-400">Naive (all GPT-4o)</span><span className="text-slate-300">${ci.modes.maximum.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs mt-0.5"><span className="text-slate-400">Savings</span><span className="text-emerald-400 font-bold">${savedVsMax.toFixed(2)} ({savedPct}%)</span></div>
            <div className="mt-2 h-2 rounded-full bg-black/40 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#14F0C8]" style={{ width: `${savedPct}%` }} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Model routing breakdown</div>
            <div className="rounded-lg overflow-hidden border border-white/[0.07]" style={{ fontFamily: MONO }}>
              <div className="grid grid-cols-4 gap-1 px-2 py-1 text-[10px] text-slate-500 bg-white/[0.03]"><span>Model</span><span className="text-right">Agents</span><span className="text-right">Cost</span><span className="text-right">Lat</span></div>
              {ci.model_breakdown.map((b) => (
                <div key={b.model} className="grid grid-cols-4 gap-1 px-2 py-1 text-[11px] text-slate-300 border-t border-white/[0.04]">
                  <span className="truncate">{b.model}</span><span className="text-right">{b.agents}</span><span className="text-right">${b.cost.toFixed(3)}</span><span className="text-right">{b.latency}s</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 p-3">
            <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-semibold mb-1"><TrendingUp className="w-3.5 h-3.5" /> Value created</div>
            <div className="text-[11px] text-slate-300 space-y-0.5" style={{ fontFamily: MONO }}>
              <div className="flex justify-between"><span className="text-slate-400">Compute cost</span><span>${ci.value.compute_cost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Business value</span><span>{money(ci.value.business_value)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">ROI on compute</span><span className="text-emerald-400 font-bold">{ci.value.roi_pct.toLocaleString()}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Time to insight</span><span>{ci.value.time_to_insight_s}s</span></div>
            </div>
            <div className="text-[10px] text-slate-500 italic mt-2">Classical equivalent: {ci.value.classical_equivalent}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Finding drill-down: WHAT / WHY / HOW ─────────────────────────────────────
export function FindingDetail({ finding, onClose }) {
  const pb = finding.playbook || {};
  const roi = pb.roi || {};
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="finding-detail" className="absolute inset-y-0 right-0 w-full max-w-2xl overflow-y-auto border-l border-white/[0.1] bg-[#0B1120]/98 backdrop-blur-2xl shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-4 bg-[#0B1120]/98 backdrop-blur-2xl border-b border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onClose} data-testid="finding-detail-back" title="Back to results"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.12] text-slate-300 hover:border-[#14F0C8]/60 hover:text-[#14F0C8] transition-colors flex-shrink-0">
              <ArrowLeft className="w-4 h-4" /> <span className="text-xs font-medium">Back</span>
            </button>
            <div className="min-w-0">
              <div className="text-base font-bold text-slate-100 truncate" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Finding #{finding.rank}: {finding.title}</div>
              <div className="text-xs text-slate-400 mt-0.5 truncate" style={{ fontFamily: MONO }}>Impact: +{money(finding.impact_usd)} · Generated by {pb.generated_by || "OO-005"}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="finding-detail-close" className="text-slate-400 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 text-[13px] leading-relaxed text-slate-300">
          {finding.quantum_contribution && (
            <div className="rounded-xl bg-[#14F0C8]/[0.08] border border-[#14F0C8]/25 p-3" data-testid="finding-quantum">
              <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider text-[#14F0C8] mb-1">
                <Zap className="w-3.5 h-3.5" /> How Quantum Improved This
              </div>
              <div className="text-[12px] text-slate-300">{finding.quantum_contribution}</div>
            </div>
          )}
          <Section color="#ef4444" title="WHAT · The Problem" body={pb.what} />
          <Section color="#f59e0b" title="WHY · Root Cause" body={pb.why} />
          <div>
            <div className="text-[11px] font-bold tracking-wider mb-2" style={{ color: "#14F0C8" }}>▌ HOW · Execution Playbook</div>
            <div className="space-y-2">
              {(pb.how || []).map((s, i) => (
                <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`playbook-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-[#14F0C8]" style={{ fontFamily: MONO }}>{s.phase}</span>
                    <span className="text-[10px] text-slate-400">Owner: {s.owner}</span>
                  </div>
                  <div className="text-[12px] text-slate-200 mt-1">{s.action}</div>
                  <div className="text-[10px] text-slate-500 mt-1">Deliverable: {s.deliverable}</div>
                </div>
              ))}
            </div>
          </div>
          {roi.annual_return != null && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Expected ROI</div>
                <div className="text-2xl font-bold text-emerald-400" style={{ fontFamily: MONO }}>{roi.roi_x}x</div>
                <div className="text-[11px] text-slate-400 mt-1" style={{ fontFamily: MONO }}>
                  Investment ~{money(roi.investment)} · Return {money(roi.annual_return)}/yr · Payback {roi.payback_days} days
                </div>
              </div>
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Risk factors</div>
                <ul className="text-[11px] text-slate-400 space-y-1 list-disc list-inside">
                  {(pb.risks || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Section = ({ color, title, body }) => (
  <div>
    <div className="text-[11px] font-bold tracking-wider mb-1.5" style={{ color }}>▌ {title}</div>
    <p className="text-slate-300">{body}</p>
  </div>
);
